import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone, MilestoneMessage } from '../types';
import ConflictFlowIllustration from './ConflictFlowIllustration';
import { newestFirst } from '../utils/messageOrder';

interface ExternalDisputeModalProps {
  milestone: Milestone;
  messages: MilestoneMessage[];
  userRole: 'company' | 'person';
  onPostMessage: (message: string) => Promise<void>;
  onDownloadExport: () => Promise<void>;
  onOpenDocuments: () => void;
  onClose: () => void;
}

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('sv-SE').format(amount);

const formatDateTime = (value?: string | null) => {
  if (!value) return null;
  return new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'long',
    timeStyle: 'short'
  }).format(new Date(value));
};

export default function ExternalDisputeModal({
  milestone,
  messages,
  userRole,
  onPostMessage,
  onDownloadExport,
  onOpenDocuments,
  onClose
}: ExternalDisputeModalProps) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const isPriceAdjustment = milestone.itemType === 'variation_price_adjustment';
  const subjectTitle = isPriceAdjustment
    ? milestone.amount === 0 ? t('externalDispute.ataChange') : t('externalDispute.ataPriceChange')
    : t('externalDispute.milestoneSubject', { number: milestone.milestoneNumber });
  const disputedAt = formatDateTime(milestone.disputedAt);
  const latestCounterpartyMessage = [...messages].reverse().find(message =>
    !message.attestationAction && (
      userRole === 'person'
        ? ['company', 'builder'].includes(message.userRole)
        : message.userRole === 'person'
    )
  );

  const downloadExport = async () => {
    setIsDownloading(true);
    setDownloadError(null);
    try {
      await onDownloadExport();
    } catch {
      setDownloadError(t('externalDispute.downloadErrorText'));
    } finally {
      setIsDownloading(false);
    }
  };

  const sendReply = async (event: React.FormEvent) => {
    event.preventDefault();
    const message = reply.trim();
    if (!message) return;
    setIsSending(true);
    setSendError(null);
    try {
      await onPostMessage(message);
      setReply('');
    } catch {
      setSendError(t('externalDispute.sendErrorText'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, overflow: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="external-dispute-title"
      onClick={onClose}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" onClick={event => event.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="external-dispute-title">
              <i className="feather-x-octagon text-danger me-2"></i>
              {t('externalDispute.modalTitle', { subject: subjectTitle })}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label={t('externalDispute.closeAriaLabel')}></button>
          </div>

          <div className="modal-body">
            <div className="bg-light rounded p-3 mb-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <small className="text-muted d-block">{t('externalDispute.descriptionLabel')}</small>
                  <span className="fw-semibold">{milestone.description}</span>
                </div>
                <div className="col-md-6">
                  <small className="text-muted d-block">{t('externalDispute.amountLabel')}</small>
                  <span className="fw-bold text-primary">{formatAmount(milestone.amount)} kr</span>
                </div>
                {disputedAt && (
                  <div className="col-md-6">
                    <small className="text-muted d-block">{t('externalDispute.disputeRegisteredLabel')}</small>
                    <span>{disputedAt}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="alert alert-danger d-flex gap-2 align-items-start" role="status">
              <i className="feather-lock mt-1"></i>
              <div>
                <div className="fw-semibold">{t('externalDispute.externalHeading')}</div>
                <div className="small mt-1">
                  {t('externalDispute.externalBody')}
                </div>
              </div>
            </div>

            <ConflictFlowIllustration currentStep="external" viewerSide={userRole} />

            {milestone.disputeReason && (
              <div className="border rounded p-3 mb-3">
                <div className="small text-muted mb-1">{t('externalDispute.originalReasonLabel')}</div>
                <div>{milestone.disputeReason}</div>
              </div>
            )}

            <section className="border border-primary rounded p-3 mb-3" aria-labelledby="external-dispute-dialogue-title">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <h6 className="mb-0" id="external-dispute-dialogue-title">
                  <i className="feather-message-circle text-primary me-2"></i>
                  {t('externalDispute.dialogueHeading')}
                </h6>
                <span className="badge bg-primary">{messages.length}</span>
              </div>
              <p className="small text-muted mb-3">{t('externalDispute.dialogueBody')}</p>

              {latestCounterpartyMessage && (
                <div className="alert alert-primary py-2" role="status" data-testid="counterparty-message-notice">
                  <div className="fw-semibold small">{t('externalDispute.counterpartyMessageTitle')}</div>
                  <div className="small mt-1">“{latestCounterpartyMessage.message}”</div>
                </div>
              )}

              {messages.length > 0 && (
                <small className="text-muted d-block text-end mb-1">{t('common.newestFirst')}</small>
              )}
              {/* Latest first, as in the objection and conflict dialogue (ClickUp 869fgj86n). */}
              {messages.length > 0 && (
                <div className="border rounded mb-3" style={{ maxHeight: 180, overflowY: 'auto' }} data-testid="external-dispute-message-list">
                  {newestFirst(messages).map(message => (
                    <div className="p-2 border-bottom" key={message.id}>
                      <div className="d-flex justify-content-between gap-2">
                        <span className="fw-semibold small">{message.userName}</span>
                        <span className="text-muted small">
                          {new Date(message.createdAt).toLocaleString('sv-SE', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="small mt-1">{message.message}</div>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={sendReply}>
                <label className="form-label fw-semibold" htmlFor="external-dispute-reply">
                  {t('externalDispute.replyLabel')}
                </label>
                <textarea
                  id="external-dispute-reply"
                  className="form-control"
                  rows={3}
                  value={reply}
                  onChange={event => setReply(event.target.value)}
                  placeholder={t('externalDispute.replyPlaceholder')}
                  disabled={isSending}
                  autoFocus
                  data-testid="conflict-reply-input"
                />
                {sendError && <div className="text-danger small mt-2" role="alert">{sendError}</div>}
                <button
                  type="submit"
                  className="btn btn-primary btn-sm mt-2"
                  disabled={isSending || !reply.trim()}
                  data-testid="conflict-reply-submit"
                >
                  <i className="feather-send me-1"></i>
                  {isSending ? t('externalDispute.sendingReply') : t('externalDispute.sendReply')}
                </button>
              </form>
            </section>

            <div className="border rounded p-3">
              <div className="d-flex gap-2 align-items-start">
                <i className="feather-file-text text-primary mt-1"></i>
                <div>
                  <div className="fw-semibold">{t('externalDispute.exportHeading')}</div>
                  <div className="small text-muted mt-1">
                    {t('externalDispute.exportBody')}
                  </div>
                </div>
              </div>
            </div>

            {downloadError && (
              <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                {downloadError}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onClose}>
              {t('externalDispute.closeButton')}
            </button>
            <button type="button" className="btn btn-outline-primary" onClick={onOpenDocuments}>
              <i className="feather-folder me-1"></i>
              {t('externalDispute.viewDocumentsButton')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void downloadExport()}
              disabled={isDownloading}
            >
              <i className="feather-download me-1"></i>
              {isDownloading ? t('externalDispute.downloading') : t('externalDispute.downloadExportButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
